/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    LexicalTypeaheadMenuPlugin,
    MenuOption,
    useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { $createMentionNode } from './nodes/MentionNode';

import { makeStyles } from 'tss-react/mui';
import {useUserSearchUsernameRequest} from "../../request/api";

const styles = (theme) => ({
    mentionsMenu: {
        backgroundColor: 'white',
        width: '20vw',
        maxHeight: "10vh",
        boxShadow: 'rgba(0, 0, 0, 0.117647) 0px 1px 6px, rgba(0, 0, 0, 0.117647) 0px 1px 4px',
        borderRadius: '4px',
        padding: "0",
        position: 'absolute', // Position for overlay
        zIndex: 1500, // Ensure it's above other elements
        top: '0',
        left: '0',
        overflow: "scroll",
        padding: "0",
        margin: "0",
        [theme.breakpoints.down('sm')]: {
            width: "50vw",
        }
    },
    mentionsMenuList: {
        margin: "0",
        padding: "0",
        display: "flex",
        flexDirection: "column",
    },
    mentionsMenuItem: {
        fontFamily: theme.typography.fontFamily,
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        cursor: 'pointer',
        zIndex: `50 !important`,
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    selectedMentionsMenuItem: {
        fontFamily: theme.typography.fontFamily,
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        cursor: 'pointer',
        backgroundColor: '#f5f5f5',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    text: {
        fontFamily: theme.typography.fontFamily,
    },
    iconUser: {
        padding: "3px",
    }
});
const useStyles = makeStyles()(styles);

const PUNCTUATION =
    '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']';

const DocumentMentionsRegex = {
    NAME,
    PUNCTUATION,
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['@'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]';

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
    '(?:' +
    '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
    ' |' + // E.g. " " in "Josh Duck"
    '[' +
    PUNC +
    ']|' + // E.g. "-' in "Salier-Hellendag"
    ')';

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
    '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$',
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
    '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$',
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

function useMentionLookupService(mentionString) {
    const {
        data: usernames,
        mutate: searchUsername,
        error: searchError,
    } = useUserSearchUsernameRequest({
        q: mentionString,
    }, undefined, { withCredentials: true });

    return usernames ?? [];
}

function checkForAtSignMentions(
    text,
    minMatchLength,
) {
    let match = AtSignMentionsRegex.exec(text);

    if (match === null) {
        match = AtSignMentionsRegexAliasRegex.exec(text);
    }
    if (match !== null) {
        // The strategy ignores leading whitespace but we need to know it's
        // length to add it to the leadOffset
        const maybeLeadingWhitespace = match[1];

        const matchingString = match[3];
        if (matchingString.length >= minMatchLength) {
            return {
                leadOffset: match.index + maybeLeadingWhitespace.length,
                matchingString,
                replaceableString: match[2],
            };
        }
    }
    return null;
}

function getPossibleQueryMatch(text) {
    return checkForAtSignMentions(text, 1);
}

class MentionTypeaheadOption extends MenuOption {
    name;
    picture;

    constructor(name, picture) {
        super(name);
        this.name = `@${name}`; // @name;
        this.picture = picture;
    }
}

function MentionsTypeaheadMenuItem({
    index,
    isSelected,
    onClick,
    onMouseEnter,
    option,
}) {
    const { classes } = useStyles();
    return (
        <li
            key={option.key}
            tabIndex={-1}
            className={isSelected ? classes.selectedMentionsMenuItem : classes.mentionsMenuItem}
            ref={option.setRefElement}
            role="option"
            aria-selected={isSelected}
            id={'typeahead-item-' + index}
            onMouseEnter={onMouseEnter}
            onClick={onClick}>
            {option.picture}
            <span className={classes.text}>{option.name}</span>
        </li>
    );
}

export default function MentionsPlugin() {
    const { classes } = useStyles();
    const [editor] = useLexicalComposerContext();
    const [queryString, setQueryString] = useState(null);

    const results = useMentionLookupService(queryString);

    const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
        minLength: 0,
    });

    const options = useMemo(
        () =>
            results
                .map(
                    (result) =>
                        new MentionTypeaheadOption(result, <i className={classes.iconUser} />),
                )
                .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
        [results],
    );

    const onSelectOption = useCallback(
        (
            selectedOption,
            nodeToReplace,
            closeMenu,
        ) => {
            editor.update(() => {
                const mentionNode = $createMentionNode(selectedOption.name);
                if (nodeToReplace) {
                    nodeToReplace.replace(mentionNode);
                }
                mentionNode.select();
                closeMenu();
            });
        },
        [editor],
    );

    const checkForMentionMatch = useCallback(
        (text) => {
            const slashMatch = checkForSlashTriggerMatch(text, editor);
            if (slashMatch !== null) {
                return null;
            }
            return getPossibleQueryMatch(text);
        },
        [checkForSlashTriggerMatch, editor],
    );

    return (
        <LexicalTypeaheadMenuPlugin
            onQueryChange={setQueryString}
            onSelectOption={onSelectOption}
            triggerFn={checkForMentionMatch}
            options={options}
            menuRenderFn={(
                anchorElementRef,
                { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
            ) =>
                anchorElementRef && results.length
                    ? ReactDOM.createPortal(
                        <div className={classes.mentionsMenu}>
                            <ul className={classes.mentionsMenuList}>
                                {options.map((option, i) => (
                                    <MentionsTypeaheadMenuItem
                                        index={i}
                                        isSelected={selectedIndex === i}
                                        onClick={() => {
                                            setHighlightedIndex(i);
                                            selectOptionAndCleanUp(option);
                                        }}
                                        onMouseEnter={() => {
                                            setHighlightedIndex(i);
                                        }}
                                        key={option.key}
                                        option={option}
                                    />
                                ))}
                            </ul>
                        </div>,
                        anchorElementRef.current,
                    )
                    : null
            }
        />
    );
}
